import { isProgramRoleRoute } from '@bright-web-skeleton/shared';
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { useRole, type AppRole } from '../hooks/useRole';
import { getAuthStatus } from '../utils/authStatus';
import { VerifyEmailScreen } from './VerifyEmailScreen';
import { supabase } from '@bright-web-skeleton/supabase';

/**
 * Props for ProtectedRoute component
 */
export interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: AppRole;
  redirectTo?: string;
  /** If true, require user to be onboarded (have app profile). If false, allow access without onboarding. */
  requireOnboarding?: boolean;
  /** Path to redirect to if user needs onboarding */
  onboardingPath?: string;
  /** If true, require email verification for email/password users. OAuth users are always verified. Default: true */
  requireEmailVerification?: boolean;
  /** App name shown on the verify email screen */
  appName?: string;
  /**
   * When true, the profile-existence gate reads the canonical global profile
   * from `public.members` keyed by `user_id = auth.uid()` instead of the
   * per-app `appProfile` (which is derived from `app_user_map`).
   *
   * Required for Kloutforce so users who completed Profile Identity on
   * another app (e.g. Kloutsale) are recognized as onboarded without
   * needing a Kloutforce-scoped `app_user_map` row.
   *
   * Default: false (legacy per-app behavior, used by Katalag/Koordinator).
   */
  useGlobalProfile?: boolean;
}

function nonEmpty(v: string | null | undefined): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Canonical global-profile completeness (mirrors Kloutforce
 * `isGlobalProfileComplete`). avatar_url is NOT required. state_code is
 * required only when country_code = 'US'.
 */
function isGlobalProfileComplete(m: Record<string, any> | null): boolean {
  if (!m) return false;
  if (!m.onboarding_completed_at) return false;
  if (!nonEmpty(m.primary_handle)) return false;
  if (!nonEmpty(m.display_name) && !nonEmpty(m.username)) return false;
  if (!nonEmpty(m.region)) return false;
  if (!nonEmpty(m.country_code)) return false;
  if (String(m.country_code).toUpperCase() === 'US' && !nonEmpty(m.state_code)) return false;
  return true;
}

/**
 * ProtectedRoute component that guards routes based on authentication, role, onboarding, and email verification.
 * 
 * CRITICAL: While loading is true, ALWAYS shows spinner - never redirects.
 * This prevents race conditions during OAuth callbacks.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  redirectTo = '/login',
  requireOnboarding = true,
  onboardingPath = '/onboarding',
  requireEmailVerification = true,
  appName,
  useGlobalProfile = false,
}): React.ReactElement => {
  const { authUser, appProfile, loading, isSessionExpired } = useAuth();
  const { role, loading: roleLoading } = useRole();
  const location = useLocation();

  // Global-profile fallback: when `useGlobalProfile` is enabled, read the
  // canonical `public.members` row by `user_id`. This is the source of truth
  // for "global profile complete" across apps. Cached per auth user id for
  // the lifetime of the component to avoid re-querying on every render.
  const [globalState, setGlobalState] = useState<{
    checked: boolean;
    complete: boolean;
    forUserId: string | null;
  }>({ checked: false, complete: false, forUserId: null });

  useEffect(() => {
    if (!useGlobalProfile) return;
    if (loading) return;
    const uid = authUser?.id ?? null;
    if (!uid) {
      setGlobalState({ checked: true, complete: false, forUserId: null });
      return;
    }
    if (globalState.checked && globalState.forUserId === uid) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('members')
        .select('onboarding_completed_at, primary_handle, display_name, username, region, country_code, state_code, created_at, primary_katalog_id')
        .eq('user_id', uid)
        .order('onboarding_completed_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) {
        console.warn('[ProtectedRoute] global profile read failed:', error.message);
        setGlobalState({ checked: true, complete: false, forUserId: uid });
        return;
      }
      const rows = (data as Record<string, any>[] | null) || [];
      if (rows.length > 1) {
        console.warn(`[ProtectedRoute] duplicate members rows (${rows.length}) for user ${uid} — selecting best complete row`);
      }
      // Best-row selection: prefer any complete row (per canonical rule).
      // If none are complete, treat as incomplete.
      const completeRow = rows.find((r) => isGlobalProfileComplete(r)) || null;
      setGlobalState({ checked: true, complete: !!completeRow, forUserId: uid });
    })();
    return () => { cancelled = true; };
  }, [useGlobalProfile, loading, authUser?.id, globalState.checked, globalState.forUserId]);

  // CRITICAL: While auth is loading, ALWAYS show spinner - never redirect
  if (loading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Helper: check for stored role-return target
  const getStoredRoleReturn = (): string | null => {
    try { return sessionStorage.getItem('kloutforce:authReturnTo'); } catch { return null; }
  };

  // If session expired, redirect to login with state
  if (isSessionExpired) {
    console.log('[HOME REDIRECT TRACE] component=ProtectedRoute reason=sessionExpired pathname=', location.pathname, 'stored=', getStoredRoleReturn());
    return (
      <Navigate 
        to={redirectTo} 
        replace 
        state={{ from: location.pathname, sessionExpired: true }} 
      />
    );
  }

  // No user after loading is complete - redirect to login
  if (!authUser) {
    console.log('[HOME REDIRECT TRACE] component=ProtectedRoute reason=noUser pathname=', location.pathname, 'stored=', getStoredRoleReturn());
    return (
      <Navigate 
        to={redirectTo} 
        replace 
        state={{ from: location.pathname }} 
      />
    );
  }

  // Email verification gate (OAuth users are always verified, so they skip this)
  if (requireEmailVerification) {
    const status = getAuthStatus(authUser);
    if (status.needsEmailVerification) {
      return <VerifyEmailScreen appName={appName} />;
    }
  }

  // Check role requirement
  if (requiredRole && role !== requiredRole) {
    console.warn(`[ProtectedRoute] User lacks required role: ${requiredRole}`);
    return <Navigate to="/unauthorized" replace />;
  }

  // Resolve "profile complete" signal. When `useGlobalProfile` is set, the
  // canonical signal is the global members row (independent of any per-app
  // app_user_map mapping). While the global read is still in flight, show
  // the spinner instead of bouncing through /onboarding.
  if (useGlobalProfile && authUser && !globalState.checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  const profileComplete = useGlobalProfile ? globalState.complete : !!appProfile;

  // Check onboarding requirement — skip if user has a pending purchase intent
  if (requireOnboarding && !profileComplete) {
    // Check for purchase intent in localStorage (katalog:authIntent)
    // This prevents onboarding from interrupting buy/wishlist flows
    let hasPendingPurchase = false;
    try {
      const raw = localStorage.getItem('katalog:authIntent');
      if (raw) {
        const intent = JSON.parse(raw);
        hasPendingPurchase = ['cart', 'wishlist', 'checkout', 'buy_now', 'exclusive_access'].includes(intent?.kind);
      }
    } catch { /* ignore */ }

    if (!hasPendingPurchase && !location.pathname.startsWith('/onboarding')) {
      // ROLE-RETURN OVERRIDE: If a role-entry target exists, redirect there instead of onboarding
      const storedReturn = getStoredRoleReturn();
      if (storedReturn) {
        const isProgramRoute = isProgramRoleRoute(storedReturn);
        if (isProgramRoute) {
          console.log('[ROLE RETURN OVERRIDE] component=ProtectedRoute overriding onboarding redirect with stored role-return:', storedReturn);
          return <Navigate to={storedReturn} replace />;
        }
        // Guard against redirect loops: if the stored return matches the
        // current location, clear it instead of bouncing on it again.
        if (storedReturn === location.pathname) {
          try { sessionStorage.removeItem('kloutforce:authReturnTo'); } catch { /* noop */ }
        }
      }
      console.log('[HOME REDIRECT TRACE] component=ProtectedRoute reason=requireOnboardingNoProfile pathname=', location.pathname, 'stored=', storedReturn);
      return <Navigate to={onboardingPath} replace />;
    }
  }

  // If user is onboarded but on an onboarding route, redirect to home
  if (!requireOnboarding && profileComplete && location.pathname.startsWith('/onboarding')) {
    // ROLE-RETURN OVERRIDE: Check for stored role-entry target before defaulting to /home
    const storedReturn = getStoredRoleReturn();
    if (storedReturn && storedReturn.startsWith('/') && !storedReturn.startsWith('//')) {
      console.log('[ROLE RETURN OVERRIDE] component=ProtectedRoute overriding /home redirect with stored role-return:', storedReturn);
      try { sessionStorage.removeItem('kloutforce:authReturnTo'); } catch { /* noop */ }
      return <Navigate to={storedReturn} replace />;
    }
    console.log('[HOME REDIRECT TRACE] component=ProtectedRoute reason=onboardedOnOnboardingRoute pathname=', location.pathname, 'stored=', storedReturn);
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
